/**
 * Phase 6b2 PR 3 — Locked kudo type taxonomy.
 *
 * The 6 vanilla types are migration-required per direction.md §3 and
 * the Phase 7.5 deep-audit tracker's worked-example rubric:
 *
 *   - The taxonomy is a FEATURE (workflow + persisted enum keys +
 *     locked copy strings) — required to port verbatim. Athletes who
 *     received a "fire 🔥 Great Run" kudo in vanilla must see the
 *     same type rendered the same way in React.
 *   - The animation grammar is PRESENTATION — open to redesign (and
 *     is, per `motion/kudos-celebration.md`).
 *
 * Source of truth: `src/renderer/js/team/team-management.js:151-158`.
 * `api/index.js:4699` carries the same emoji map server-side for the
 * notification body, so any rename here must land in both files.
 *
 * The persisted enum key (`id` here) is what the server stores in
 * `team_kudos.kudo_type`. Glyph + label are presentation-only.
 */

export const KUDO_TYPES = [
  { id: 'fire', emoji: '🔥', label: 'Great Run' },
  { id: 'beast', emoji: '💪', label: 'Beast Mode' },
  { id: 'trophy', emoji: '🏆', label: 'PR Crusher' },
  { id: 'star', emoji: '⭐', label: 'Consistent' },
  { id: 'heart', emoji: '❤️', label: 'Team Player' },
  { id: 'lightning', emoji: '⚡', label: 'Improved' },
] as const;

export type KudoType = (typeof KUDO_TYPES)[number];
export type KudoTypeKey = KudoType['id'];

/** Constant-time lookup for emoji + label by persisted key. */
const _MAP: Record<KudoTypeKey, KudoType> = KUDO_TYPES.reduce(
  (acc, t) => {
    acc[t.id] = t;
    return acc;
  },
  {} as Record<KudoTypeKey, KudoType>,
);

/** Returns the type definition for a persisted key, or null if the
 *  server somehow returned an unknown enum value (defensive — server
 *  validates against `kudoEmojis` map at api/index.js:4699). */
export function lookupKudoType(key: string | null | undefined): KudoType | null {
  if (!key) return null;
  return _MAP[key as KudoTypeKey] ?? null;
}

/** Type guard for unknown strings that may come from the API. */
export function isKudoTypeKey(key: string | null | undefined): key is KudoTypeKey {
  if (!key) return false;
  return key in _MAP;
}

/** Max length of the optional message field, locked from vanilla
 *  (`(message || '').slice(0, 140)` at api/index.js:4694). */
export const MAX_KUDO_MESSAGE_LENGTH = 140;
